import { Box, Button, Divider, IconButton, Pagination, Stack, TextField, Tooltip, Typography } from "@mui/material";
import LoadingCircle from "../../utils/LoadingCircle";
import Toast from "../../utils/Toast";
import { useEffect, useRef, useState } from "react";
import { ModelHandler } from "../../interface/model-handler";
import { v4 as uuidv4 } from 'uuid';

import AiMore from "../aiUtils/AiMore";
import "./MakePressCheck.css";
import TaskOutlinedIcon from '@mui/icons-material/TaskOutlined';
import IssueBlock from "./IssueBlock";
import CopyToClipboard from "react-copy-to-clipboard";
import { useLocation, useNavigate } from "react-router-dom";
import { HttpMainApi } from "../../interface/main-api";
import { HttpCustomApi } from "../../interface/custom-api";
import { naverAiApiUrl, userState } from "../../interface/MainInterface";
import ContentPasteSearchIcon from "@mui/icons-material/ContentPasteSearch";
import { HttpNaverAiApi } from "../../interface/naver-ai-api";
import { Vectocore } from "vectocore";

interface propsType {
  userState: userState;
}

interface ErrorItem {
  id: string;
  errorContent: string;
  errorType: string;
  replacement?: string[];
  helpText?: string;
}

const getErrorClass = (type: string) => {
  switch (type) {
    case "어법 오류": return "grammar-error";
    case "문맥상 오류": return "context-error";
    case "표현상 오류": return "expression-error";
  }
};

const ErrorTag = ({ color }: { color: string }) => (
  <Box sx={{
    width: 10, height: 10, borderRadius: '50%', backgroundColor: color, display: 'inline-block', mr: 1
  }} />
);

const mainApi = new HttpMainApi();
const naverApi = new HttpNaverAiApi();
const vectocore = new Vectocore({ baseURL: process.env.REACT_APP_VECTOCORE_BASE_URL, tenantKey: process.env.REACT_APP_VECTOCORE_TENANT_KEY });
// const vectoCoreApi = new HttpCustomApi(process.env.REACT_APP_VECTOR_CORE);

const SpellCheck = (props: propsType) => {
  const navigate = useNavigate();
  const toastRef: any = useRef();
  const resultRef: any = useRef();
  const location = useLocation();

  const [isLoading, setIsLoading] = useState(false);
  const [editMode, setEditMode] = useState(true);
  const [text, setText] = useState(location.state?.pressInfo?.result ?? "");
  const [result, setResult] = useState("");
  const [errors, setErrors] = useState<ErrorItem[]>([]);
  // const [pressHistory, setPressHistory] = useState<string[]>([]);
  // const [historyPage, setHistoryPage] = useState(0);

  const [model, setModel] = useState("");
  const modelHandler = new ModelHandler(model);
  const [aiApi, handleStreamResponse] = modelHandler.getModelElements();
  const [streamReader, setStreamReader] = useState<ReadableStreamDefaultReader | null>(null);

  useEffect(() => {
    modelHandler
    .getUsingModel()
    .then((usingModel) => {
      setModel(usingModel);
    })
    .catch((errorMsg: any) => {
      toastRef.current?.toast("" + errorMsg, "error", 4000, {
        vertical: "top",
        horizontal: "center",
      });
    });

    return () => {
      setIsLoading(false);
      setResult("");
      // setPressHistory([]);
      // setHistoryPage(1);
      setEditMode(true);
    };
  }, []);

  useEffect(() => {
    if (!editMode) {
      // 여기서 setResult
      setResult(() => applyHighlights(text, errors));
    }
  }, [errors]);

  const genStreamResponse = async (streamRes: ReadableStream) => {
    const reader = streamRes?.getReader();
    const decoder = new TextDecoder();

    let done = false;
    let content = "";

    while (!done) {
      const { value, done: doneReading } = await reader.read();
      done = doneReading; // 스트림 완료시 true
      const chunkValue = decoder.decode(value);
      content = content + chunkValue;
    }
    try {
      content = content
        .replace(/^```json\s*/, '')  // 앞부분 제거
        .replace(/```$/, '')         // 뒷부분 제거
        .trim();
      const payload: any = typeof content === 'string' ? JSON.parse(content) : content;

      return payload;
    } catch (error) {
      // console.log("content: ", content);
      // console.log("content Type: ", typeof content);
      return { code: "500", response: { error_msg: "json parse error" } };
    }
  };

  const refinePress = async () => {
    setIsLoading(true);
    setEditMode(true);

    const param = {
      command: "refine_press",
      text: text
    };

    try {
      const streamResponse = await aiApi.postStream(param);
      setIsLoading(false);

      handleStreamResponse(
        streamResponse, 
        setText,
        setStreamReader,
        (finalText: string) => {
          // 스트리밍 종료 시 실행될 콜백
          // if (pressHistory.length === 0) {
          //   setPressHistory([text, finalText]);
          //   // setResult(finalText);
          //   setHistoryPage(2);
          // } else {
          //   setPressHistory((prev) => [...prev, finalText]); // 최신 text를 저장
          //   setHistoryPage(pressHistory.length + 1);
          // }
          setResult(finalText);
          setEditMode(false);
        }
      );
    } catch (error) {
      console.error("Error fetching press refine data: ", error);
    } finally {
      setIsLoading(false);
    }
  }

  const spellCheck_naver = async () => {
    setIsLoading(true);

    const param = {
      command: "spell_check_v2",
      text: text
    };

    try {
      const response = await naverApi.postStream(param);
      console.log("response: ", response);
      if (response.code === '200') {
        // const res = await genStreamResponse(streamResponse);
        let res = response.response;
        console.log("res: ", res);

        const parsed = JSON.parse(res);
        console.log("parsed: ", parsed);
        
        // errorContent → uuid 매핑
        const errorIdMap = new Map<string, string>();
        const errorsWithId = parsed.content.map((error: ErrorItem) => {
          let id = errorIdMap.get(error.errorContent);
          if (!id) {
            id = uuidv4();
            errorIdMap.set(error.errorContent, id);
          }
          return { ...error, id };
        });
        
        setErrors(errorsWithId);
        setEditMode(false);
      }
    } catch (error) {
      console.error("Error fetching spell check: ", error);
    }

    setIsLoading(false);
  };

  const spellCheck = async () => {
    setIsLoading(true);

    const param = {
      command: "spell_check_v2",
      text: text
    };

    try {
      const streamResponse = await aiApi.postStream(param);
      const res = await genStreamResponse(streamResponse);
      console.log("res: ", res);
      if (res) {
        // errorContent → uuid 매핑
        const errorIdMap = new Map<string, string>();
        const errorsWithId = res.content.map((error: ErrorItem) => {
          let id = errorIdMap.get(error.errorContent);
          if (!id) {
            id = uuidv4();
            errorIdMap.set(error.errorContent, id);
          }
          return { ...error, id };
        });
        
        setErrors(errorsWithId);
        setEditMode(false);
      }
    } catch (error) {
      console.error("Error fetching spell check: ", error);
    }

    setIsLoading(false);
  };

  const applyHighlights = (text: string, errors: ErrorItem[]) => {
    if (!errors.length) return text;

    let result = '';
    let cursor = 0;

    // 중복 제거된 오류 목록 (errorContent 기준)
    const uniqueErrors = Array.from(new Map(errors.map(e => [e.errorContent, e])).values());

    // errorContent → uuid 매핑
    // const errorIdMap = new Map<string, string>();
    // uniqueErrors.forEach(error => {
    //   errorIdMap.set(error.errorContent, uuidv4());
    // }) 

    // 오류 위치 수집
    const ranges: { start: number, end: number, error: ErrorItem }[] = [];
    for (const error of uniqueErrors) {
      const re = new RegExp(error.errorContent.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g');
      let match;
      while ((match = re.exec(text)) !== null) {
        // const sharedId = errorIdMap.get(error.errorContent) ?? uuidv4();
        // const errorWithId = { ...error, id: sharedId };
        ranges.push({ start: match.index, end: match.index + match[0].length, error });
      }
    }

    // 위치 기준 정렬 및 중첩 방지
    ranges.sort((a, b) => a.start - b.start);
    const filteredRanges: typeof ranges = [];
    let lastEnd = -1;
    for (const range of ranges) {
      if (range.start >= lastEnd) {
        filteredRanges.push(range);
        lastEnd = range.end;
      }
    }

    // 문자열 생성
    for (const range of filteredRanges) {
      if (cursor < range.start) {
        result += text.slice(cursor, range.start);
      }

      const errorText = text.slice(range.start, range.end);
      // result += `<span class="highlight-text ${getErrorClass(range.error.errorType)} ${range.error.id}">${errorText}</span>`;
      result += `<span class="highlight-selected ${range.error.id}">${errorText}</span>`;

      cursor = range.end;
    }

    // 마지막 남은 텍스트 추가
    if (cursor < text.length) {
      result += text.slice(cursor);
    }

    return result;
  };

  const applyCorrection = (className: string, newText: string) => {
    const container = resultRef.current;
    if (!container) return;

    // 비어 있는 highlight-text span 제거
    removeEmptyHighlightSpans(container);

    const elements = container.getElementsByClassName(className);
    if (elements.length === 0) return;

    // HTMLCollection → 배열로 변환 후 텍스트 일괄 변경
    Array.from(elements).forEach((el: any) => {
      el.textContent = newText;
      el.classList.add('modified');
    });

    // 변경된 DOM의 innerHTML을 상태로 반영
    setResult(container.innerHTML);
    setText(container.innerText);
    // setPressHistory(prev =>
    //   prev.map((item, index) => 
    //     index === historyPage - 1 ? container.innerText: item
    //   )
    // );
  };

  const removeEmptyHighlightSpans = (container: HTMLElement) => {
    const spans = container.querySelectorAll('span.highlight-text');
    spans.forEach((span: any) => {
      if (!span.innerText || span.innerText.trim() === '') {
        span.remove();
      }
    });
  };

  // 히스토리 페이지 이동시 결과 동기화
  const handleHistoryPage = (event: any, value: number) => {
    // setHistoryPage(value);
    // setResult(pressHistory[value - 1]);
    // setText(pressHistory[value - 1]);
    setErrors([]);
  }
  
  const changeResult = async (newResult: string) => {
    // console.log("newResult: ", newResult);
    // console.log("innerText: ", resultRef.current.innerText);
    setText(resultRef.current.innerText);
    // setPressHistory(prevHistory =>
    //   prevHistory.map((item, i) => (i === historyPage - 1 ? resultRef.current.innerText : item))
    // );
  };

   const handleCompletePress = async () => {
    await modifyResult();
    await completePress();
    window.location.href = `/myTotalList`;
  };

  // 수정본 결과 저장 
  const modifyResult = async () => {
    let newPressInfo = { ...location.state.pressInfo };
    newPressInfo.result = text;
    // console.log("modifyResult newPressInfo: ", newPressInfo);

    // setPressInfo(newPressInfo);
    const param: any = {
      command: "put_press_info",
      press_info: newPressInfo
    };

    await mainApi.post(param);
    // getPressInfo(newPressInfo.sk);
  };

  // 홍보실 최종 완료
  const completePress = async () => {
    if (
      !window.confirm(
        "최종 완료하시겠습니까?\n최종 완료된 보도자료는 더이상 수정할 수 없습니다."
      )
    )
      return;
    
    // console.log("completePress pressInfo: ", pressInfo);
    setIsLoading(true);

    let newPressInfo = { ...location.state.pressInfo };
    newPressInfo.result = text;

    // 테마 임베딩
    let ebdText: any = newPressInfo.form_data;
    ebdText.title = newPressInfo.title;
    
    const ebdParam = {
      command: "keywords",
      model: "gpt-4o",
      text: JSON.stringify(ebdText)
    }

    const vectorRes = await aiApi.postStream(ebdParam);
    console.log("vectorRes : ", vectorRes);
    if (vectorRes.code !== "200") {
      console.error(vectorRes.response.error_msg);
      toastRef.current?.toast(
        "" + vectorRes.response.error_msg,
        "error",
        4000,
        { vertical: "top", horizontal: "center" }
      );
      setIsLoading(false);
      return;
    }
    
    const itneParam: any = {
      indexName: "press_release_embeddings",
      docBody: {
        "id": newPressInfo.sk,
        "keywords": vectorRes.response.keywords,
        "data": newPressInfo
      }
    };
    
    const vectorResult = await vectocore.putExtreamData(itneParam);
    console.log("vectorResult : ", vectorResult);
    if (vectorResult.code !== "200") {
      console.error(vectorResult.response.error_msg);
      toastRef.current?.toast(
        "" + vectorResult.response.error_msg,
        "error",
        4000,
        { vertical: "top", horizontal: "center" }
      );
      setIsLoading(false);
      return;
    }
    
    // 홍보실 전송 comment 저장
    // 보도자료 상태 변경
    const param: any = {
      command: "complete_press",
      press_info: newPressInfo,
      comment_info: {
        user_id: props.userState.id,
        user_name: props.userState.name,
        type: "complete",
        comment: "",
      },
    };
    
      const res = await mainApi.post(param);
      if (res.code === "200") {
        // getPressInfo(pressInfo.sk);
      } else {
        console.error(res.response.error_msg);
        toastRef.current?.toast("" + res.response.error_msg, "error", 4000, {
          vertical: "top",
          horizontal: "center",
        });
      }
      setIsLoading(false);
    };

  const goAiDesk = async () => {
    if (
      !window.confirm(
        "현재 작성 중인 내용이 있습니다.\n저장 후 AI 데스킹 화면으로 이동하시겠습니까?"
      )
    )
      return;
    await modifyResult();
    navigate(`/aiDesk/${location.state?.pressInfo.sk}`);
  };

  return (
    <>
      <Box sx={{ p: 0, height: "100%" }}>
        <Stack direction={"row"} spacing={0} sx={{ height: "100%" }}>
          <Stack
            direction={"column"}
            spacing={0}
            sx={{
              width: "600px",
              minWidth: "600px",
              p: "32px",
              pb: "16px !important",
            }}
          >
            <Box className="sub-header-root" sx={{ justifyContent: 'space-between' }}>
              <div style={{ display: "flex", flexDirection: "row" }}>
                <Typography
                  variant="h5"
                  gutterBottom
                  className="sub-header-title"
                >
                  교정 문서
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: "8px", marginLeft: "12px" }}>
                  <CopyToClipboard
                    text={resultRef.current?.innerText || ""}
                    onCopy={() => {
                      toastRef.current?.toast(
                        "보도자료를 복사했습니다.",
                        "success",
                        3000,
                        {
                          vertical: "top",
                          horizontal: "center",
                        }
                      );
                    }}
                  >
                    <Tooltip title="복사하기">
                      <IconButton sx={{ p: 0 }}>
                        <img 
                          src="/images/icon-copy-op.png" 
                          alt="Copy Icon" 
                        />
                      </IconButton>
                    </Tooltip>
                  </CopyToClipboard>
                  <>
                    <Tooltip title={'글자수: ' + (resultRef.current?.innerText.length || text.length) + '자'}>
                      <IconButton sx={{ p: 0 }}>
                        <img
                          src="/images/icon-information-op.png"
                          alt="Copy Icon"
                        />
                      </IconButton>
                    </Tooltip>
                  </>
                </Box>
              </div>
              {props.userState.isAdmin && location.state?.pressInfo && (
                <Box sx={{ display: 'flex', flexDirection: 'row', gap: 0.5}}>
                  <Box 
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      fontSize: '14px', 
                      fontWeight: '600', 
                      color: '#5D88F1', 
                      fontFamily: 'Pretendard',
                      padding: '7px 12px',
                      backgroundColor: '#5D88F11A',
                      borderRadius: '8px',
                      cursor: 'pointer'
                    }}
                    onClick={goAiDesk}
                  >
                    {/* <ContentPasteSearchIcon sx={{ width: "14px", height: "14px", color: "#5D88F1" }} /> */}
                    AI 데스킹
                  </Box>
                  <Box 
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      fontSize: '14px', 
                      fontWeight: '500', 
                      color: '#fff', 
                      fontFamily: 'Pretendard',
                      padding: '7px 12px',
                      backgroundColor: '#5D88F1',
                      borderRadius: '8px',
                      cursor: 'pointer'
                    }}
                    onClick={() => handleCompletePress()}
                  >
                    {/* 적용 */}
                    {/* <TaskOutlinedIcon 
                      sx={{
                        color: "#fff",
                        fontSize: 15, // 아이콘 크기 조절 (선택)
                        // mr: '0px',   // 왼쪽으로 살짝 당겨서 간격 줄이기 (선택)
                      }}
                    /> */}
                    완료
                  </Box>
                </Box>
              )}
            </Box>
            <Box 
              className="sub-content-root" 
              sx={{ 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column' ,
              }}
            >
              <TextField
                size="small"
                variant="standard"
                InputProps={{
                  disableUnderline: true,
                  sx: {
                    height: '100%',
                    alignItems: 'flex-start', // 텍스트가 상단 정렬되도록
                    padding: 0,
                  },
                }}
                fullWidth
                multiline
                rows={1}
                placeholder={"내용을 입력하세요."}
                value={text}
                onChange={(e) => {
                  setText(e.target.value);
                }}
                type="search"
                autoComplete="off"
                inputProps={{ enterKeyHint: "enter", style: { height: '100%' } }}
                sx={{
                  display: editMode ? 'flex' : 'none',
                  flex: 1,
                  textarea: {
                    height: '100%',
                    overflow: 'auto',
                    resize: 'none',

                    // 스크롤바 스타일 적용
                    // FireFox 
                    scrollbarWidth: 'thin',
                    scrollbarColor: '#ECECEC transparent',

                    // WebKit (Chrome, Safari)
                    '&::-webkit-scrollbar': {
                      width: '6px',
                    },
                    '&::-webkit-scrollbar-track': {
                      background: 'transparent', // 트랙 배경 투명
                    },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: '#ECECEC', // 필요 시 스크롤바 컬러 조절
                      borderRadius: '3px',
                    },
                  },
                  marginBottom: '104px',
                }}
              />
              <Box
                ref={resultRef}
                id={"resultBox"}
                className="result-content new-line"
                dangerouslySetInnerHTML={{ __html: result }}
                // sx={!editMode ? { display: "" } : { display: "none" }}
                sx={{
                  display: editMode ? "none" : "",
                  fontSize: "1rem",         // TextField size="small" 기준
                  fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                  fontWeight: 400,
                  lineHeight: "1.4375em",       // 텍스트 높이 맞춤
                  letterSpacing: "0.00938em",
                  color: "rgba(0, 0, 0, 0.87)",
                  padding: "4px 0",             // TextField padding과 유사
                  minHeight: "1.4375em",        // 텍스트 높이 확보
                  width: "100%",

                  // 스크롤바 스타일 적용
                  overflow: 'auto',
                  scrollbarWidth: 'thin', // Firefox용
                  scrollbarColor: '#ECECEC transparent', // Firefox용

                  '&::-webkit-scrollbar': {
                    width: '6px', // WebKit용 너비
                  },
                  '&::-webkit-scrollbar-track': {
                    background: 'transparent', // WebKit용 트랙
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: '#ECECEC', // WebKit용 스크롤바 색
                    borderRadius: '3px',
                  },
                }}
              />
              {/* 왼쪽 하단 영역 */}
              <Box className="template-footer">
                <Stack
                  direction={"column"}
                  spacing={2}
                  sx={{ width: "100%", alignItems: "center" }}
                >
                  {/* 히스토리 페이징 */}
                  {/* {pressHistory.length > 0 && (
                    <Pagination
                      count={pressHistory.length}
                      siblingCount={3}
                      color="primary"
                      page={historyPage}
                      onChange={handleHistoryPage}
                    />
                   )
                  } */}
                  
                  {/* 버튼 영역 */}
                  <div className="creat-btn-wrap" style={{ position: 'relative' }}>
                    {/* 전체 수정 버튼 */ }
                    {/* <button
                      className="add-file btn-ghost"
                      onClick={() => {
                        // fncUpload();
                        refinePress();
                      }}
                      // disabled={click}
                    >
                      전체 수정하기
                    </button> */}
                    {/* 보도자료 생성 버튼 */}
                    <button
                      className="create btn-second"
                      onClick={() => {spellCheck();}}
                      title={"검사하기"}
                      disabled={isLoading}
                    >
                      {"검사하기"}
                    </button>
                  </div>
                </Stack>
              </Box>
            </Box>
          </Stack>
          {/* 구분선 */}
          <Divider orientation="vertical" variant="middle" flexItem />
          {/* llm 결과 */}
          <Stack
            direction={"column"}
            spacing={0}
            sx={{ width: "100%", p: "32px", pb: "16px !important" }}
          >
            <Box className="sub-header-root">
              <Stack direction={"row"} spacing={2}>
                <Typography
                  variant="h5"
                  gutterBottom
                  className="sub-header-title"
                >
                  맞춤법 검사
                </Typography>
                  <>
                    
                  </>
              </Stack>
            </Box>
            <Box className="sub-content-root">
              <Box sx={{ 
                position: 'relative', 
                height: 'calc(100% - 1rem)', 
                display: 'flex', 
                flexDirection: 'column' 
              }}>
                <Box sx={{
                  flex: 1,
                  overflowY: 'auto',
                  paddingRight: '1rem',
                  paddingBottom: '6rem', // 여유 공간 확보: 아래 고정된 박스와 겹치지 않도록

                  // 스크롤바 스타일 적용
                  overflow: 'auto',
                  scrollbarWidth: 'thin', // Firefox용
                  scrollbarColor: '#ECECEC transparent', // Firefox용

                  '&::-webkit-scrollbar': {
                    width: '6px', // WebKit용 너비
                  },
                  '&::-webkit-scrollbar-track': {
                    background: 'transparent', // WebKit용 트랙
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: '#ECECEC', // WebKit용 스크롤바 색
                    borderRadius: '3px',
                  },
                }}>
                  {errors.map((error: any, index: number) => (
                    <>
                      <IssueBlock
                        error={error}
                        onApplyCorrection={applyCorrection}
                      />
                      <Divider sx={{ mt: 2, mb: 2 }}/>
                    </>
                  ))}

                  {!editMode && result.length == 0 && (
                    <Typography fontWeight="400" fontSize="1rem">탐지된 오류가 없습니다.</Typography>
                  )}
                </Box>

                {/* <Box sx={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  backgroundColor: '#fff',
                  padding: '2rem 0rem',
                  // boxShadow: '0 -2px 8px rgba(0,0,0,0.1)',
                  zIndex: 10,
                }}> */}
                  {/* <Divider sx={{ my: 2 }} /> */}
                  {/* <Stack direction="row" spacing={2}>
                    <Stack direction="row" alignItems="center">
                      <ErrorTag color="#e53935" />
                      <Typography fontWeight="500" fontSize="1rem">어법 오류</Typography>
                    </Stack>
                    <Stack direction="row" alignItems="center">
                      <ErrorTag color="#4caf50" />
                      <Typography fontWeight="500" fontSize="1rem">문맥상 오류</Typography>
                    </Stack>
                    <Stack direction="row" alignItems="center">
                      <ErrorTag color="#495EFF" />
                      <Typography fontWeight="500" fontSize="1rem">표현상 오류</Typography>
                    </Stack>
                  </Stack>
                </Box> */}
              </Box>
            </Box>
          </Stack>
        </Stack>
      </Box>
      {/* 로딩바 */}
      <LoadingCircle loading={isLoading} />
      {/* 알림 토스트 */}
      <Toast ref={toastRef} />
      {/* <AiMore
        model={model}
        eventId={"resultBox"}
        getFormData={""} //참고자료
        originResult={result} //전체보도자료
        resultRef={resultRef}
        setResult={setResult}
        changeResult={changeResult}
      /> */}
    </>
  )
}

export default SpellCheck;